import { Box, Grid, GridItem, Icon, SimpleGrid, Stack } from '@chakra-ui/react';
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { BiPlus } from 'react-icons/bi';
import { MdAdd } from 'react-icons/md';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftTag from '@/components/Atoms/FaceliftTag/FaceliftTag';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftTextArea from '@/components/Atoms/FaceliftTextArea';
import FaceliftDatePicker from '@/components/Molecules/FaceliftDatePicker';
import FaceliftDragDrop from '@/components/Molecules/FaceliftDragDrop';
import { FilePreviewList } from '@/components/Molecules/FilePreviewList';
import { Stepper, type StepperStep } from '@/components/Organisms/Stepper';
import type { UploadedFile } from '@/hooks';
import { useTourContext } from '@/store/tourContext';

export interface DemoBVFormHandle {
	goToStep: (step: number) => void;
	addWound: () => void;
	removeWound: () => void;
}

/**
 * Demo-only Benefit Verification form.
 * - Uses static mock data (no API calls, no validations).
 * - Exposes stable refs for the tutorial system (BV*Ref keys).
 * - The goal is purely to drive the guided tour, not to submit real data.
 */
const DemoBVFormInner = (
	_: unknown,
	ref: React.Ref<DemoBVFormHandle | null>,
) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [totalWounds, setTotalWounds] = useState(1);
	const { registerElement } = useTourContext();

	// Refs for tutorial steps (these names must match backend "reference" keys)
	const [
		clinicFieldRef,
		providerFieldRef,
		patientDropdownRef,
		addNewPatientButtonRef,
		productDropdownRef,
		woundDetailsRef,
		wound1Ref,
		wound2Ref,
		addWoundButtonRef,
		addWoundButtonRef2,
		uploadDocumentsRef,
	] = [
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLButtonElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLButtonElement | null>(null),
		useRef<HTMLButtonElement | null>(null),
		useRef<HTMLDivElement | null>(null),
	];

	// Register demo BV elements with the tour system whenever the step changes.
	// This ensures that elements that only exist on certain steps (e.g. Patient,
	// Add New Patient, Product, Wound, Upload) are registered once they are mounted.
	useEffect(() => {
		if (clinicFieldRef.current) {
			registerElement('BVClinicFieldRef', clinicFieldRef.current);
		}
		if (providerFieldRef.current) {
			registerElement('BVProviderFieldRef', providerFieldRef.current);
		}
		if (patientDropdownRef.current) {
			registerElement('BVPatientDropdownRef', patientDropdownRef.current);
		}
		if (addNewPatientButtonRef.current) {
			registerElement(
				'BVAddNewPatientButtonRef',
				addNewPatientButtonRef.current,
			);
		}
		if (productDropdownRef.current) {
			registerElement('BVProductDropdownRef', productDropdownRef.current);
		}
		if (woundDetailsRef.current) {
			registerElement('BVWoundDetailsRef', woundDetailsRef.current);
		}
		if (wound1Ref.current) {
			registerElement('BVWoundDetailsRef1', wound1Ref.current);
		}
		if (wound2Ref.current) {
			registerElement('BVWoundDetailsRef2', wound2Ref.current);
		}
		if (addWoundButtonRef.current) {
			registerElement('BVAddWoundButtonRef', addWoundButtonRef.current);
			// Also register the second reference pointing to the same button
			registerElement('BVAddWoundButtonRef2', addWoundButtonRef.current);
		}
		// Also register addWoundButtonRef2 if it's available (both point to same button)
		if (addWoundButtonRef2.current) {
			registerElement('BVAddWoundButtonRef2', addWoundButtonRef2.current);
		}
		if (uploadDocumentsRef.current) {
			registerElement('BVUploadDocumentsRef', uploadDocumentsRef.current);
		}

		return () => {
			registerElement('BVClinicFieldRef', null);
			registerElement('BVProviderFieldRef', null);
			registerElement('BVPatientDropdownRef', null);
			registerElement('BVAddNewPatientButtonRef', null);
			registerElement('BVProductDropdownRef', null);
			registerElement('BVWoundDetailsRef', null);
			registerElement('BVWoundDetailsRef1', null);
			registerElement('BVWoundDetailsRef2', null);
			registerElement('BVAddWoundButtonRef', null);
			registerElement('BVAddWoundButtonRef2', null);
			registerElement('BVUploadDocumentsRef', null);
		};
	}, [
		registerElement,
		currentStep,
		clinicFieldRef,
		providerFieldRef,
		patientDropdownRef,
		addNewPatientButtonRef,
		productDropdownRef,
		woundDetailsRef,
		wound1Ref,
		wound2Ref,
		addWoundButtonRef,
		addWoundButtonRef2,
		uploadDocumentsRef,
		totalWounds,
	]);

	useEffect(() => {
		if (addWoundButtonRef.current && totalWounds < 3) {
			const timeoutId = setTimeout(() => {
				registerElement('BVAddWoundButtonRef', addWoundButtonRef.current);
				registerElement('BVAddWoundButtonRef2', addWoundButtonRef.current);
			}, 100);
			return () => clearTimeout(timeoutId);
		}
	}, [totalWounds, addWoundButtonRef, registerElement]);

	// Simple mock data – realistic but static
	const clinicOptions: SelectItem[] = [
		{ label: 'Downtown Wound Care Clinic', value: 'clinic-001' },
		{ label: 'Lakeside Vascular Center', value: 'clinic-002' },
	];

	const providerOptions: SelectItem[] = [
		{ label: 'Dr. Amelia Carter, DPM', value: 'provider-001' },
		{ label: 'Dr. Michael Sanchez, MD', value: 'provider-002' },
	];

	const patientOptions: SelectItem[] = [
		{ label: 'John Doe', value: 'patient-001' },
		{ label: 'Emily Johnson', value: 'patient-002' },
		{ label: 'Robert Miller', value: 'patient-003' },
	];

	const productOptions: SelectItem[] = [
		{ label: 'GrafixPL Prime 4x4cm', value: 'prod-001' },
		{ label: 'GrafixPL Core 2x3cm', value: 'prod-002' },
		{ label: 'GrafixPL Prime 6x8cm', value: 'prod-003' },
	];

	useImperativeHandle(
		ref,
		() => ({
			goToStep: (step: number) => {
				const next = Math.min(Math.max(step, 1), 6);
				setCurrentStep(next);
			},
			addWound: () => {
				setTotalWounds((prev) => {
					if (prev < 2) {
						return Math.min(prev + 1, 2);
					}
					return prev; 
				});
			},
			removeWound: () => {
				setTotalWounds((prev) => {
					if (prev >= 2) {
						return 1;
					}
					return prev; 
				});
			},
		}),
		[],
	);

	const renderFacilityInformation = () => (
		<Box>
			<FaceliftText
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="semibold"
				mb={{ base: 4, md: 6 }}
				fontStyle="italic"
			>
				Facility Information
			</FaceliftText>

			<Grid
				templateColumns={{
					base: '1fr',
					sm: 'repeat(1, 1fr)',
					md: 'repeat(2, 1fr)',
				}}
				gap={{ base: 4, sm: 5, md: 6 }}
				alignItems="end"
			>
				<GridItem ref={clinicFieldRef}>
					<FaceliftSelect<SelectItem, false>
						limit={15}
						topLabel="Select Clinic"
						items={clinicOptions}
						value={clinicOptions[0]}
						isSearchable={false}
					/>
				</GridItem>

				<GridItem ref={providerFieldRef}>
					<FaceliftSelect<SelectItem, false>
						limit={15}
						topLabel="Provider"
						items={providerOptions}
						value={providerOptions[0]}
						isSearchable={false}
					/>
				</GridItem>
			</Grid>
		</Box>
	);

	const renderPatientInformation = () => (
		<>
			<FaceliftText
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="semibold"
				mb={{ base: 4, md: 6 }}
				fontStyle="italic"
			>
				Patient Information
			</FaceliftText>

			<Grid
				templateColumns={{
					base: '1fr',
					sm: 'repeat(1, 1fr)',
					md: 'repeat(3, 1fr)',
				}}
				gap={{ base: 4, sm: 5, md: 6 }}
				alignItems="start"
			>
				{/* Row 1 */}
				{/* Select Patient */}
				<GridItem
					display={'flex'}
					justifyContent={'space-between'}
					alignItems={'center'}
					gap={3}
				>
					<Box width={'full'} ref={patientDropdownRef}>
						<FaceliftSelect<SelectItem, false>
							limit={10}
							topLabel="Patient"
							value={patientOptions[0]}
							items={patientOptions}
							placeholder="Type Patient Name"
							ai
							aiSuggestionText='Use a keyword like "Smith" to see more relevant results'
						/>
						
					</Box>
					<Box ref={addNewPatientButtonRef} style={{ marginTop: '-50px' }}>
						<FaceliftIconButton
							size={'2xs'}
							aria-label="Add Patient"
							mb={1}
						>
							<MdAdd />
						</FaceliftIconButton>
					</Box>
				</GridItem>

				{/* Primary Insurance */}
				<GridItem>
					<FaceliftSelect<SelectItem, false>
						limit={15}
						topLabel="Primary Insurance"
						isClearable
						value={{ label: 'Medicare (Part B)', value: 'medicare-b' }}
						items={[
							{ label: 'Medicare (Part B)', value: 'medicare-b' },
							{ label: 'United Healthcare', value: 'uhc' },
						]}
						placeholder="Select Primary Insurance"
						ai
						aiSuggestionText='Use a keyword like "United" to see more relevant results'
					/>
				</GridItem>

				{/* Secondary Insurance */}
				<GridItem>
					<FaceliftSelect<SelectItem, false>
						limit={15}
						topLabel="Secondary Insurance"
						value={{
							label: 'Select Secondary Insurance',
							value: 'none',
						}}
						items={[
							{ label: 'Select Secondary Insurance', value: 'none' },
							{ label: 'Aetna', value: 'aetna' },
						]}
						placeholder="Select Secondary Insurance"
						ai
						aiSuggestionText='Use a keyword like "United" to see more relevant results'
					/>
				</GridItem>

				{/* Row 2 */}
				{/* DOB */}
				<GridItem>
					<FaceliftDatePicker
						topLabel="DOB"
						value="11/10/1995"
						placeholder="MM/DD/YYYY"
					/>
				</GridItem>

				{/* Primary Member ID */}
				<GridItem>
					<FaceliftInput
						px={3}
						topLabel="Primary Member ID"
						placeholder="Add Primary Member ID"
						value="0021845CH2"
					/>
				</GridItem>

				{/* Secondary Member ID */}
				<GridItem>
					<FaceliftInput
						px={3}
						topLabel="Secondary Member ID"
						placeholder="Add Secondary Member ID"
						value=""
					/>
				</GridItem>

				{/* Row 3 */}
				{/* EMR ID */}
				<GridItem mt={5}>
					<FaceliftInput
						px={3}
						topLabel="EMR ID"
						placeholder="EMR ID"
						value=""
					/>
				</GridItem>

				{/* Is Patient Residing in a Nursing Home or Facility? */}
				<GridItem>
					<FaceliftSelect<SelectItem, false>
						limit={10}
						topLabel="Is Patient Residing in a Nursing Home or Nursing Facility?"
						value={{ label: 'No', value: 'no' }}
						items={[
							{ label: 'No', value: 'no' },
							{ label: 'Yes', value: 'yes' },
						]}
						placeholder="Yes/No"
					/>
				</GridItem>

				{/* If Yes, has it been over 100 Days? */}
				<GridItem mt={5}>
					<FaceliftSelect<SelectItem, false>
						topLabel="If Yes, has it been over 100 Days?"
						value={{ label: 'No', value: 'no' }}
						items={[
							{ label: 'No', value: 'no' },
							{ label: 'Yes', value: 'yes' },
						]}
						placeholder="Yes/No"
					/>
				</GridItem>
			</Grid>
		</>
	);

	const renderProductStep = () => (
		<Box>
			<FaceliftText
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="semibold"
				mb={{ base: 4, md: 6 }}
				fontStyle="italic"
			>
				Product
			</FaceliftText>

			{/* Product Selection */}
			<Box mb={{ base: 6, md: 10 }} ref={productDropdownRef}>
				<FaceliftSelect
					limit={15}
					topLabel="Product (s)"
					placeholder="Select Products"
					items={productOptions}
					value={productOptions.slice(0, 2)}
					isMulti={true}
				/>
			</Box>

			<Box>
				<FaceliftTextArea
					topLabel="Notes"
					value="Chart notes reviewed for coverage criteria and medical necessity."
					placeholder="Type Here"
					onChange={() => {
						// No-op: demo-only component, we do not store this value
					}}
				/>
			</Box>
		</Box>
	);

	const renderWoundInformation = () => {
		// Mock options to emulate real dropdowns
		const mockApplicationCodes: SelectItem[] = [
			{ label: '15275 - Application lower leg', value: '15275' },
			{ label: '15276 - Application foot', value: '15276' },
		];
		const mockDiagnosisCodes: SelectItem[] = [
			{
				label: 'L97.221 - Non-pressure chronic ulcer of left calf',
				value: 'L97.221',
			},
			{
				label: 'L97.222 - Non-pressure chronic ulcer of other calf',
				value: 'L97.222',
			},
			{
				label: 'L97.223 - Non-pressure chronic ulcer of unspecified calf',
				value: 'L97.223',
			},
		];
		const mockWoundTypes: SelectItem[] = [
			{ label: 'Venous Leg Ulcer', value: 'venous-leg-ulcer' },
			{ label: 'Diabetic Foot Ulcer', value: 'diabetic-foot-ulcer' },
		];

		return (
			<Box >
				<FaceliftText
					fontSize={{ base: 'sm', md: 'md' }}
					fontWeight="semibold"
					color={{ base: 'gray.800', _dark: 'gray.100' }}
					mb={{ base: 4, md: 6 }}
					fontStyle="italic"
				>
					Wound Information
				</FaceliftText>
				<Box overflow="auto" minH={600} maxH={650} ref={woundDetailsRef}>
					{[1, 2, 3]
						.filter((index) => index <= totalWounds)
						.map((index) => (
							<Box 
								key={index} 
								mb={2}
								ref={index === 1 ? wound1Ref : index === 2 ? wound2Ref : null}
							>
								<FaceliftText
									fontSize={{ base: 'sm', md: 'md' }}
									fontWeight="semibold"
									color={{ base: 'gray.800', _dark: 'gray.100' }}
									mb={4}
								>
									Wound {index}
								</FaceliftText>
								<Grid
									templateColumns={{
										base: '1fr',
										sm: 'repeat(1, 1fr)',
										md: 'repeat(3, 1fr)',
									}}
									gap={{ base: 4, sm: 5, md: 6 }}
								>
									{/* Row 1 */}
									{index === 1 && (
										<GridItem>
											<FaceliftDatePicker
												topLabel="Procedure Date"
												placeholder="MM/DD/YYYY"
												value="12/02/2025"
											/>
										</GridItem>
									)}
									<GridItem>
										<FaceliftSelect<SelectItem, false>
											limit={15}
											topLabel="Application Code 1"
											items={mockApplicationCodes}
											value={mockApplicationCodes[0]}
											placeholder="Select Code"
											ai
											aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
										/>
									</GridItem>
									<GridItem>
										<FaceliftSelect<SelectItem, false>
											limit={15}
											topLabel="Application Code 2"
											items={mockApplicationCodes}
											value={mockApplicationCodes[1]}
											placeholder="Select Code"
											ai
											aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
										/>
									</GridItem>

									{/* Row 2 */}
									<GridItem>
										<FaceliftInput
											topLabel="Wound Length (cm)"
											placeholder="Add Length"
											value={index === 1 ? '2.8' : index === 2 ? '1.9' : '1.3'}
											type="text"
										/>
									</GridItem>
									<GridItem>
										<FaceliftInput
											topLabel="Wound Width (cm)"
											placeholder="Add Width"
											value={index === 1 ? '2.1' : index === 2 ? '1.4' : '1.0'}
											type="text"
										/>
									</GridItem>
									<GridItem>
										<FaceliftInput
											topLabel="Wound Size (cm)"
											placeholder="Add Size"
											value={
												index === 1 ? '5.88' : index === 2 ? '2.66' : '1.30'
											}
											type="number"
											disabled
										/>
									</GridItem>
									<GridItem>
										<FaceliftInput
											topLabel="Wound Depth (cm)"
											placeholder="Add Depth"
											value={index === 1 ? '0.2' : index === 2 ? '0.1' : '0.1'}
											type="text"
										/>
									</GridItem>

									{/* Row 3 */}
									<GridItem>
										<FaceliftSelect<SelectItem, false>
											limit={15}
											topLabel="Primary Diagnosis"
											items={mockDiagnosisCodes}
											value={mockDiagnosisCodes[0]}
											placeholder="Select Diagnosis"
											ai
											aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
										/>
									</GridItem>
									<GridItem>
										<FaceliftSelect<SelectItem, false>
											limit={15}
											topLabel="Diagnosis code 2"
											items={mockDiagnosisCodes}
											value={mockDiagnosisCodes[1]}
											placeholder="Select Code"
											ai
											aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
										/>
									</GridItem>
									<GridItem>
										<FaceliftSelect<SelectItem, false>
											limit={15}
											topLabel="Diagnosis code 3"
											items={mockDiagnosisCodes}
											value={mockDiagnosisCodes[2]}
											placeholder="Select Code"
											ai
											aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
										/>
									</GridItem>
									<GridItem>
										<FaceliftSelect<SelectItem, false>
											limit={15}
											topLabel="Diagnosis code 4"
											items={mockDiagnosisCodes}
											value={null}
											placeholder="Select Code"
											ai
											aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
										/>
									</GridItem>
									<GridItem>
										<FaceliftSelect<SelectItem, false>
											limit={15}
											topLabel="Wound Type"
											items={mockWoundTypes}
											value={mockWoundTypes[0]}
											placeholder="Select Wound Type"
										/>
									</GridItem>
								</Grid>
							</Box>
						))}
				</Box>
				<FaceliftButton
					bg={'transparent'}
					color="brand.solid"
					border={'1px solid'}
					_hover={{ color: 'brand.solidSecondary', cursor: 'pointer' }}
					display={totalWounds < 3 ? 'inline-block' : 'none'}
					onClick={() => setTotalWounds((prev) => Math.min(prev + 1, 3))}
					my={2}
					ref={addWoundButtonRef || addWoundButtonRef2}
				>
					Add Wound{' '}
					<Icon>
						<BiPlus />
					</Icon>
				</FaceliftButton>
			</Box>
		);
	};

	const renderUploadDocuments = () => {
		// Mock uploaded files for demo
		const mockFiles: UploadedFile[] = [
			{
				id: 'file-1',
				name: 'ChartNotes_JohnDoe_2025-02-10.pdf',
				size: 245760,
				type: 'application/pdf',
				file: null,
				sharePointUrl: 'https://mock.sharepoint.com/file1',
				sharePointFileName: 'ChartNotes_JohnDoe_2025-02-10.pdf',
				clinic: 'clinic-001',
				folderName: 'Client Inbox',
			},
			{
				id: 'file-2',
				name: 'WoundPhotos_LeftCalf_01.png',
				size: 1024000,
				type: 'image/png',
				file: null,
				sharePointUrl: 'https://mock.sharepoint.com/file2',
				sharePointFileName: 'WoundPhotos_LeftCalf_01.png',
				clinic: 'clinic-001',
				folderName: 'Client Inbox',
			},
			{
				id: 'file-3',
				name: 'InsuranceCard_Front_Back.pdf',
				size: 512000,
				type: 'application/pdf',
				file: null,
				sharePointUrl: 'https://mock.sharepoint.com/file3',
				sharePointFileName: 'InsuranceCard_Front_Back.pdf',
				clinic: 'clinic-001',
				folderName: 'Client Inbox',
			},
		];

		const formatFileSize = (bytes: number): string => {
			if (bytes === 0) return '0 Bytes';
			const k = 1024;
			const sizes = ['Bytes', 'KB', 'MB', 'GB'];
			const i = Math.floor(Math.log(bytes) / Math.log(k));
			return Math.round((bytes / k ** i) * 100) / 100 + ' ' + sizes[i];
		};

		return (
			<Box ref={uploadDocumentsRef}>
				<FaceliftText
					fontSize={{ base: 'sm', md: 'md' }}
					fontWeight="semibold"
					mb={{ base: 4, md: 6 }}
					fontStyle="italic"
				>
					Upload Documents
				</FaceliftText>

				{/* Drag Drop Component */}
				<FaceliftDragDrop
					onFilesUpload={() => {
						// No-op: demo-only component
					}}
					multiple
					maxSize={104857600}
					disabled={true}
					disabledMessage="This is a demo form. File uploads are disabled."
				/>

				{/* Uploaded Files List */}
				<FilePreviewList files={mockFiles} formatFileSize={formatFileSize} />
			</Box>
		);
	};

	const renderReviewStep = () => (
		<Stack gap={4}>
			<FaceliftText fontWeight="semibold" fontSize="lg">
				Review (Demo)
			</FaceliftText>
			<FaceliftText fontSize="sm" color="gray.600">
				This screen summarizes static mock data so the tutorial can point to a
				realistic review step without saving anything.
			</FaceliftText>

			<SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
				<Box>
					<FaceliftText fontWeight="medium">Clinic</FaceliftText>
					<FaceliftText>Downtown Wound Care Clinic</FaceliftText>
				</Box>
				<Box>
					<FaceliftText fontWeight="medium">Provider</FaceliftText>
					<FaceliftText>Dr. Amelia Carter, DPM</FaceliftText>
				</Box>
				<Box>
					<FaceliftText fontWeight="medium">Patient</FaceliftText>
					<FaceliftText>John Doe</FaceliftText>
				</Box>
				<Box>
					<FaceliftText fontWeight="medium">Primary Insurance</FaceliftText>
					<FaceliftText>United Healthcare Medicare Advantage</FaceliftText>
				</Box>
			</SimpleGrid>

			<Box>
				<FaceliftText fontWeight="medium" mb={2}>
					Products
				</FaceliftText>
				<Stack direction="row" gap={2} flexWrap="wrap">
					<FaceliftTag label="GrafixPL Prime 4x4cm" colorIndex={0} />
					<FaceliftTag label="GrafixPL Core 2x3cm" colorIndex={1} />
				</Stack>
			</Box>

			<Box>
				<FaceliftText fontWeight="medium" mb={2}>
					Wounds Summary
				</FaceliftText>
				<FaceliftText fontSize="sm" color="gray.700">
					{totalWounds} wound(s) with mock size and diagnosis codes have been
					pre-populated for tutorial purposes.
				</FaceliftText>
			</Box>
		</Stack>
	);

	const steps: StepperStep[] = [
		{ name: 'Facility Information', element: renderFacilityInformation() },
		{ name: 'Patient Information', element: renderPatientInformation() },
		{ name: 'Product', element: renderProductStep() },
		{ name: 'Wound Information', element: renderWoundInformation() },
		{ name: 'Upload Documents', element: renderUploadDocuments() },
		{ name: 'Review', element: renderReviewStep() },
	];

	return (
		<Box>
			<Stepper
				steps={steps}
				controlButtonsPosition="left"
				stepOverride={currentStep}
				onStepChange={setCurrentStep}
				submitButtonText="Finish (Demo Only)"
				onSubmit={() => {}}
			/>
		</Box>
	);
};

const DemoBVForm = forwardRef<DemoBVFormHandle, unknown>(DemoBVFormInner);

export default DemoBVForm;
